import { forwardRef } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { SessionTimelineEvent } from '@kirin/api-client/contracts/agent-sessions';
import { formatSubPolicyLabel } from '@kirin/api-client/helpers/alerts';
import { Badge } from '@/components/ui/badge';
import { useAlertSessionEvent } from '@/hooks/api/useAgentSessionTimeline';
import { useDisplayTimeZone } from '@/hooks/useDisplayTimeZone';
import { cn } from '@/lib/utils';
import {
  BLOCKED_OUTCOME,
  eventBadgeLabel,
  formatClockTime,
  formatOffsetFromAlert,
  rowFinding,
} from './session-timeline-display';
import { describeEventDetail } from './session-event-details';

export interface SessionTimelineRowProps {
  event: SessionTimelineEvent;
  organizationId: string | null;
  occurrenceId: string;
  highlighted: boolean;
  alertOccurredAt: string | null;
  expanded: boolean;
  onToggle: () => void;
}

function EventPayload({
  organizationId,
  occurrenceId,
  eventId,
}: {
  organizationId: string | null;
  occurrenceId: string;
  eventId: string;
}) {
  const { data, isLoading, isError } = useAlertSessionEvent(
    organizationId,
    occurrenceId,
    eventId
  );
  if (isLoading) {
    return <p className="text-xs text-muted-foreground">Loading payload…</p>;
  }
  if (isError || !data) {
    return (
      <p className="text-xs text-destructive">Could not load this event.</p>
    );
  }
  const fields = describeEventDetail(data);
  if (fields.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        No details were recorded for this event.
      </p>
    );
  }
  return (
    <dl
      className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-xs"
      data-testid="session-event-details"
    >
      {fields.map((field, i) => (
        <div key={`${field.label}-${i}`} className="contents">
          <dt className="font-medium">{field.label}:</dt>
          <dd className="min-w-0">
            {field.value.kind === 'code' ? (
              <pre className="max-h-60 overflow-auto whitespace-pre-wrap break-all rounded bg-muted p-2 font-mono">
                {field.value.text}
              </pre>
            ) : (
              <p className="max-h-60 overflow-auto whitespace-pre-wrap break-words">
                {field.value.text}
              </p>
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export const SessionTimelineRow = forwardRef<
  HTMLLIElement,
  SessionTimelineRowProps
>(function SessionTimelineRow(
  {
    event,
    organizationId,
    occurrenceId,
    highlighted,
    alertOccurredAt,
    expanded,
    onToggle,
  },
  ref
) {
  const finding = rowFinding(event);
  const blocked = finding?.outcome === BLOCKED_OUTCOME;
  const offset = formatOffsetFromAlert(event.occurredAt, alertOccurredAt);
  const timeZone = useDisplayTimeZone();
  return (
    <li
      ref={ref}
      data-testid="session-timeline-row"
      data-event-id={event.eventId}
      data-highlighted={highlighted || undefined}
      data-blocked={blocked || undefined}
      data-detected={(finding && !blocked) || undefined}
      className={cn(
        'rounded-md border px-3 py-2',
        blocked && 'border-destructive/60 bg-destructive/5',
        highlighted && 'ring-2 ring-primary'
      )}
    >
      <button
        type="button"
        className="flex w-full items-start gap-2 text-left"
        aria-expanded={expanded}
        onClick={onToggle}
      >
        {expanded ? (
          <ChevronDown className="mt-0.5 size-4 shrink-0" />
        ) : (
          <ChevronRight className="mt-0.5 size-4 shrink-0" />
        )}
        <time
          dateTime={event.occurredAt}
          title={offset}
          // w-40: "11:30:05 PM GMT+13:45", the longest clock, is 152px here.
          className="w-40 shrink-0 font-mono text-xs text-muted-foreground"
        >
          {formatClockTime(event.occurredAt, timeZone)}
        </time>
        <Badge variant="outline" className="shrink-0">
          {eventBadgeLabel(event)}
        </Badge>
        {blocked && (
          <Badge variant="destructive" className="shrink-0">
            Blocked
          </Badge>
        )}
        <span className="min-w-0 flex-1 truncate text-sm">
          {event.target ?? ''}
        </span>
      </button>
      {finding && (
        <p
          className={cn(
            'mt-1 pl-6 text-xs',
            blocked ? 'text-destructive' : 'text-muted-foreground'
          )}
          data-testid="session-timeline-row-finding"
        >
          <span className="font-medium">
            {blocked ? 'Block rationale:' : 'Detected:'}
          </span>{' '}
          {formatSubPolicyLabel(finding.subPolicyKey, finding.type)} —{' '}
          {finding.description}
        </p>
      )}
      {expanded && (
        <div className="mt-2 pl-6">
          <EventPayload
            organizationId={organizationId}
            occurrenceId={occurrenceId}
            eventId={event.eventId}
          />
        </div>
      )}
    </li>
  );
});
